import './WebhookInstructions.css'

const BASE_URL = window.location.origin

function WebhookInstructions() {
  return (
    <div data-testid="webhook-instructions" className="webhook-instructions">
      <section className="webhook-section">
        <h2 className="webhook-section-title">Failures</h2>

        <div className="webhook-endpoint" data-testid="webhook-endpoint-create-failure">
          <h3 className="webhook-endpoint-title">Create a Failure</h3>
          <div className="webhook-method-url">
            <span className="webhook-method post">POST</span>
            <code className="webhook-url">{BASE_URL}/api/failures</code>
          </div>
          <p className="webhook-description">Creates a new test failure entry with an associated tag.</p>
          <div className="webhook-body-label">Request body (JSON):</div>
          <pre className="webhook-code">{JSON.stringify({
            title: "string (required)",
            tag_name: "string (creates or reuses tag)",
            github_repository: "string (required) — full GitHub URL, e.g. https://github.com/acme/web-app",
            branch: "string (required) — git branch name, e.g. main",
            failing_changeset: "string (required) — commit SHA with the failing code",
            fixed_changeset: "string (required) — commit SHA with the correct fix",
            test_file: "string (required) — path to the failing test file, e.g. tests/auth.spec.ts"
          }, null, 2)}</pre>
        </div>

        <div className="webhook-endpoint" data-testid="webhook-endpoint-list-failures">
          <h3 className="webhook-endpoint-title">List Failures</h3>
          <div className="webhook-method-url">
            <span className="webhook-method get">GET</span>
            <code className="webhook-url">{BASE_URL}/api/failures</code>
          </div>
          <p className="webhook-description">Returns all failures with their latest run results and tag information.</p>
        </div>

        <div className="webhook-endpoint" data-testid="webhook-endpoint-delete-failure">
          <h3 className="webhook-endpoint-title">Delete a Failure</h3>
          <div className="webhook-method-url">
            <span className="webhook-method delete">DELETE</span>
            <code className="webhook-url">{BASE_URL}/api/failures/:id</code>
          </div>
          <p className="webhook-description">Deletes a failure by its ID.</p>
        </div>

      </section>

      <section className="webhook-section">
        <h2 className="webhook-section-title">Runs</h2>

        <div className="webhook-endpoint" data-testid="webhook-endpoint-create-run">
          <h3 className="webhook-endpoint-title">Create a Run</h3>
          <div className="webhook-method-url">
            <span className="webhook-method post">POST</span>
            <code className="webhook-url">{BASE_URL}/api/runs</code>
          </div>
          <p className="webhook-description">Creates a new benchmark run for the specified agent.</p>
          <div className="webhook-body-label">Request body (JSON):</div>
          <pre className="webhook-code">{JSON.stringify({
            agent_id: "string (required, UUID of existing agent)",
            selected_tags: ["tag name or UUID", "..."],
            selected_failures: ["failure UUID", "..."],
          }, null, 2)}</pre>
        </div>

        <div className="webhook-endpoint" data-testid="webhook-endpoint-list-runs">
          <h3 className="webhook-endpoint-title">List Runs</h3>
          <div className="webhook-method-url">
            <span className="webhook-method get">GET</span>
            <code className="webhook-url">{BASE_URL}/api/runs</code>
          </div>
          <p className="webhook-description">Returns all runs with selected tags, selected failures, and summary statistics.</p>
        </div>

        <div className="webhook-endpoint" data-testid="webhook-endpoint-get-run">
          <h3 className="webhook-endpoint-title">Get Run Details</h3>
          <div className="webhook-method-url">
            <span className="webhook-method get">GET</span>
            <code className="webhook-url">{BASE_URL}/api/runs/:id</code>
          </div>
          <p className="webhook-description">Returns detailed information for a single run including results, logs, and container executions.</p>
        </div>

        <div className="webhook-endpoint" data-testid="webhook-endpoint-delete-run">
          <h3 className="webhook-endpoint-title">Delete a Run</h3>
          <div className="webhook-method-url">
            <span className="webhook-method delete">DELETE</span>
            <code className="webhook-url">{BASE_URL}/api/runs/:id</code>
          </div>
          <p className="webhook-description">Deletes a run by its ID.</p>
        </div>
      </section>

      <section className="webhook-section">
        <h2 className="webhook-section-title">Agents</h2>

        <div className="webhook-endpoint" data-testid="webhook-endpoint-create-agent">
          <h3 className="webhook-endpoint-title">Create an Agent</h3>
          <div className="webhook-method-url">
            <span className="webhook-method post">POST</span>
            <code className="webhook-url">{BASE_URL}/api/agents</code>
          </div>
          <p className="webhook-description">Registers a new agent for benchmark runs.</p>
          <div className="webhook-body-label">Request body (JSON):</div>
          <pre className="webhook-code">{JSON.stringify({
            name: "string (required)",
            tool_command: "string (required)",
            prompt: "string (optional)"
          }, null, 2)}</pre>
        </div>

        <div className="webhook-endpoint" data-testid="webhook-endpoint-list-agents">
          <h3 className="webhook-endpoint-title">List Agents</h3>
          <div className="webhook-method-url">
            <span className="webhook-method get">GET</span>
            <code className="webhook-url">{BASE_URL}/api/agents</code>
          </div>
          <p className="webhook-description">Returns all agents with their accuracy and average cost statistics.</p>
        </div>

        <div className="webhook-endpoint" data-testid="webhook-endpoint-delete-agent">
          <h3 className="webhook-endpoint-title">Delete an Agent</h3>
          <div className="webhook-method-url">
            <span className="webhook-method delete">DELETE</span>
            <code className="webhook-url">{BASE_URL}/api/agents/:id</code>
          </div>
          <p className="webhook-description">Deletes an agent by its ID.</p>
        </div>
      </section>

      <section className="webhook-section">
        <h2 className="webhook-section-title">Webhook Events (Container Callbacks)</h2>

        <div className="webhook-endpoint" data-testid="webhook-endpoint-post-webhook-event">
          <h3 className="webhook-endpoint-title">Receive Container Event</h3>
          <div className="webhook-method-url">
            <span className="webhook-method post">POST</span>
            <code className="webhook-url">{BASE_URL}/api/webhook-events</code>
          </div>
          <p className="webhook-description">
            Set this URL as <code>webhookUrl</code> in your <code>ContainerConfig</code> from <code>@replayio/app-building</code>.
            Containers POST events here automatically during their lifecycle.
          </p>
          <div className="webhook-body-label">Request body (JSON):</div>
          <pre className="webhook-code">{JSON.stringify({
            type: "string (e.g. container.started, message.done, task.done, log)",
            containerName: "string (container name)",
            timestamp: "string (ISO-8601)",
            data: "object (event-specific payload)"
          }, null, 2)}</pre>
          <div className="webhook-body-label webhook-body-label-spaced">Event types:</div>
          <pre className="webhook-code">{`container.started  — HTTP server is listening
container.idle     — Container waiting for work
container.stopping — Container shutting down
container.stopped  — Container has stopped
message.queued     — POST /message received
message.started    — Message processing begins
message.done       — Message processing complete
message.error      — Message processing failed
task.started       — Task processing begins
task.done          — Task processing complete
log                — Each log line`}</pre>
        </div>

        <div className="webhook-endpoint" data-testid="webhook-endpoint-list-webhook-events">
          <h3 className="webhook-endpoint-title">List Webhook Events</h3>
          <div className="webhook-method-url">
            <span className="webhook-method get">GET</span>
            <code className="webhook-url">{BASE_URL}/api/webhook-events</code>
          </div>
          <p className="webhook-description">Returns webhook events for a specific container.</p>
          <div className="webhook-body-label">Query parameters:</div>
          <pre className="webhook-code">{`container_name — Name of the container (required)
limit          — max events to return (default 100, max 5000)`}</pre>
        </div>
      </section>

      <section className="webhook-section">
        <h2 className="webhook-section-title">Container Maintenance</h2>

        <div className="webhook-endpoint" data-testid="webhook-endpoint-reap-stale-containers">
          <h3 className="webhook-endpoint-title">Reap Stale Containers</h3>
          <div className="webhook-method-url">
            <span className="webhook-method post">POST</span>
            <code className="webhook-url">{BASE_URL}/api/reap-stale-containers</code>
          </div>
          <p className="webhook-description">
            Checks all containers currently in &quot;pending&quot; or &quot;running&quot; status against the Fly.io Machines API.
            Any container whose machine is no longer alive is marked as failed. For run containers, run completion
            is also checked.
          </p>
          <div className="webhook-body-label">Request body:</div>
          <pre className="webhook-code">{`No body required (empty POST).`}</pre>
          <div className="webhook-body-label webhook-body-label-spaced">Response (JSON):</div>
          <pre className="webhook-code">{JSON.stringify({
            reapedContainers: [{ containerId: "string", containerName: "string" }]
          }, null, 2)}</pre>
          <div className="webhook-body-label webhook-body-label-spaced">Example curl:</div>
          <pre className="webhook-code">{`curl -X POST ${BASE_URL}/api/reap-stale-containers`}</pre>
        </div>
      </section>

      <section className="webhook-section">
        <h2 className="webhook-section-title">Dashboard</h2>

        <div className="webhook-endpoint" data-testid="webhook-endpoint-get-dashboard">
          <h3 className="webhook-endpoint-title">Get Dashboard Statistics</h3>
          <div className="webhook-method-url">
            <span className="webhook-method get">GET</span>
            <code className="webhook-url">{BASE_URL}/api/dashboard</code>
          </div>
          <p className="webhook-description">Returns benchmark statistics per failure tag and agent, with optional run and timeframe filtering.</p>
          <div className="webhook-body-label">Query parameters:</div>
          <pre className="webhook-code">{`runId    — UUID of a specific run (defaults to latest)
timeframe — last_24h | last_7d | last_30d | all_time`}</pre>
        </div>
      </section>
    </div>
  )
}

export default WebhookInstructions
